import { IMeetingCategory } from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useState } from 'react';
import { toast } from 'sonner';
import {
	invalidateMeetingCategoriesQuery,
	useMeetingCategoriesQuery,
	useMeetingCategoryCreateMutation,
} from '../../../../../queries/Scheduler';
import { Button } from '../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { Input } from '../../../../components-v2/Input';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { MeetingCategoryRow } from './MeetingCategoryRow';
import { styleSheet } from './styles';

interface IProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	forUserId?: string;
	/** Called with the newly created category so the caller can act on it (e.g. assign it) */
	onCategoryCreated?: (category: IMeetingCategory) => void;
}

export const MeetingCategoriesDialog: FC<IProps> = ({ isOpen, onOpenChange, forUserId, onCategoryCreated }) => {
	const [newName, setNewName] = useState('');

	const categoriesQuery = useMeetingCategoriesQuery({ userId: forUserId, enabled: isOpen });
	const categories: IMeetingCategory[] = categoriesQuery.data?.values ?? [];

	const createMutation = useMeetingCategoryCreateMutation({
		onSuccess: created => {
			setNewName('');
			invalidateMeetingCategoriesQuery();
			onCategoryCreated?.(created);
		},
		onError: error => toast.error(error?.systemMessage ?? 'Failed to create the category'),
	});

	const onAdd = () => {
		const name = newName.trim();
		if (!name) {
			return;
		}
		createMutation.mutate({ category: { name }, forUserId });
	};

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Meeting Categories</DialogTitle>
					<DialogDescription>
						Meetings in the same category share the maximums you set here. When a meeting also has its own maximum, the
						stricter of the two applies.
					</DialogDescription>
				</DialogHeader>
				<DialogBody>
					{categoriesQuery.isLoading ? <LoadingSpinner type='small' /> : null}
					{!categoriesQuery.isLoading ? (
						<div className={css(styleSheet.table)}>
							{categories.length > 0 ? (
								<div className={css(styleSheet.rowHeader)}>
									<span className={css(styleSheet.headerCell)}>Name</span>
									<span className={css(styleSheet.headerCell, styleSheet.headerPerDay)}>Per day</span>
									<span className={css(styleSheet.headerCell, styleSheet.headerPerMonth)}>Per month</span>
								</div>
							) : (
								<div className={css(styleSheet.emptyState)}>
									No categories yet. Add one below, then assign meetings to it.
								</div>
							)}
							{categories.map(category => (
								<MeetingCategoryRow
									key={category.id}
									category={category}
									forUserId={forUserId}
									onChanged={invalidateMeetingCategoriesQuery}
								/>
							))}
							<div className={css(styleSheet.addRow)}>
								<Input
									aria-label='New category name'
									styles={[styleSheet.nameInput]}
									placeholder='New category name'
									value={newName}
									onChange={e => setNewName(e.target.value)}
									onKeyDown={e => {
										if (e.key === 'Enter') {
											onAdd();
										}
									}}
								/>
								<Button
									variant='secondary'
									size='sm'
									styles={[styleSheet.addRowButton]}
									onClick={onAdd}
									disabled={!newName.trim()}
									isLoading={createMutation.isPending}
								>
									Add category
								</Button>
							</div>
						</div>
					) : null}
				</DialogBody>
				<DialogFooter>
					<DialogClose render={<Button variant='secondary'>Done</Button>} />
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
